import { z } from 'zod';
import { ColorSchema, LOOK, about } from '../schema_fields.ts';
import { ColorOrNoneSchema } from '../tile_style.ts';

// What every gauge has beside its scale and colours (docs/specs/gauge-styles.md §3), for the value
// tile's gauge and the thermostat's dial. Which style draws it is the tile style's `gaugeStyle`.

/** A colour from a value on (Home Assistant's segments, Grafana's thresholds). */
export const ThresholdSchema = z.object({
    from: z.number().meta(about('From', 'the value this colour starts at')),
    color: ColorSchema.meta(about('Colour', undefined, 'color')),
}).meta({ title: 'Threshold' });

export type Threshold = z.infer<typeof ThresholdSchema>;

/**
 * The look of a gauge, shared by its styles: each uses those it has (the spec's §2 says which). Left
 * out, a style's own default.
 */
export const GaugeLookSchema = z.object({
    thickness: z.number().min(1).max(50).optional().meta(about('Thickness', 'of the arc, ring or bar, in percent of the gauge')),
    steps: z.number().int().min(2).max(100).optional().meta(about('Steps', 'ticks, blocks, cells or dots; the needle\'s numbered ticks')),
    sweep: z.number().min(90).max(360).optional().meta(about('Sweep', 'how far round a dial goes, in degrees')),
    roundEnds: z.boolean().optional().meta(about('Round ends')),
    vertical: z.boolean().optional().meta(about('Upright', 'a bar, cells or bullet standing up')),
    numbers: z.boolean().optional().meta(about('Numbers', 'on the scale, where the style has them')),
    accent: ColorSchema.optional().meta(about('Accent', 'the needle, the knob, the glow, the fill', 'color')),
    markSecond: z.boolean().optional().meta(about('Second on the scale', 'marks the second value on the scale too, where it is on the same scale (a set point); the thermostat\'s does, a value tile\'s not unless set')),
});

export type GaugeLook = z.infer<typeof GaugeLookSchema>;

/**
 * Marks a gauge's colour fields (the gradient, the thresholds, one colour, the track): the editor
 * shows them together, in one "Colours" row with the colouring (issue #24), not one row each.
 */
export const IN_GAUGE_COLOURS = { 'x-in': 'gauge-colours' } as const;

/** The gauge's colouring and the colours beside its gradient (`colors`, the tile's own), for a tile's gauge object. */
export const gaugeColourFields = {
    thresholds: z.array(ThresholdSchema).default([]).meta({ ...about('Thresholds', 'a colour from a value on, such as green, yellow from 24, red from 27'), ...IN_GAUGE_COLOURS }),
    // The editor's "Colours" row, with the fields above and below (and the gauge's style, issue #24).
    coloring: z.enum(['gradient', 'threshold', 'single']).optional().meta({
        ...about('Colouring', 'the gradient, the colour of the threshold the value is in, or one colour; by the thresholds where there are any, else one colour where one is set', 'gauge-colours'),
        'x-labels': { gradient: 'Gradient', threshold: 'By thresholds', single: 'One colour' },
        ...LOOK,
    }),
    /** The gauge in one colour (issue #23). */
    color: ColorSchema.optional().meta({ ...about('Colour', 'the gauge\'s one colour', 'color'), ...IN_GAUGE_COLOURS, ...LOOK }),
    // Left out, each gauge style draws its own (a faint white on most); "none" draws none.
    track: ColorOrNoneSchema.optional().meta({ ...about('Unfilled part', 'the part of the scale not filled', 'color'), 'x-none': "The style's own", ...IN_GAUGE_COLOURS, ...LOOK }),
};

export const gaugeLookField = GaugeLookSchema.optional().meta({ ...about('Look', 'thickness, steps, sweep and the like, where the style has them'), ...LOOK });
